import { Link } from "react-router-dom";
import { Download, ArrowUpRight } from "lucide-react";
import Seo from "@/components/seo/Seo";
import Reveal from "@/components/ui-primitives/Reveal";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import { executive, CANONICAL_ORIGIN } from "@/data/site";

const contents = [
  { id: "overview",   label: "Overview" },
  { id: "career",     label: "Career Summary" },
  { id: "philosophy", label: "Leadership Philosophy" },
  { id: "expertise",  label: "Areas of Focus" },
  { id: "beyond",     label: "Beyond the Executive Suite" },
  { id: "today",      label: "Today" },
];

export default function Biography() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    url: `${CANONICAL_ORIGIN}/leadership/biography`,
    name: `${executive.name} — Executive Biography`,
    description: executive.bio[0],
    mainEntity: { "@id": `${CANONICAL_ORIGIN}/leadership#adeshi-moffi` },
  };

  return (
    <>
      <Seo
        title="Executive Biography — Adeshi Moffi | Investors Pathway LLC"
        description="The executive biography of Adeshi Moffi, President & Managing Partner of Investors Pathway LLC. Prior experience at JPMorgan Chase & Co. and BKD. Focus areas include private equity, capital advisory, consumer brands, retail, and manufacturing."
        path="/leadership/biography"
        image={executive.headshot}
        jsonLd={jsonLd}
      />

      <section className="pt-28 md:pt-36 pb-10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <Breadcrumbs
            items={[
              { label: "Home", to: "/" },
              { label: "Leadership", to: "/leadership" },
              { label: "Biography" },
            ]}
          />
          <Reveal>
            <div className="mt-10 section-num">02 / Executive Biography</div>
            <h1
              data-testid="biography-title"
              className="h-display text-[3rem] md:text-[4.6rem] lg:text-[5.4rem] text-[#0A1128] mt-6 max-w-5xl"
            >
              {executive.name}
            </h1>
            <p className="mt-4 body-lead max-w-3xl">
              {executive.title}, {executive.company}. {executive.summary}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-8">
          {/* Sticky sidebar */}
          <aside className="col-span-12 md:col-span-3 no-print">
            <div className="md:sticky md:top-24">
              <div className="section-num">Contents</div>
              <ul className="mt-4 space-y-2 border-t border-[#E2E8F0] pt-4">
                {contents.map((c) => (
                  <li key={c.id}>
                    <a
                      href={`#${c.id}`}
                      data-testid={`toc-link-${c.id}`}
                      className="text-sm font-sans text-[#475569] hover:text-[#0A1128] tracking-wide"
                    >
                      {c.label}
                    </a>
                  </li>
                ))}
              </ul>

              <div className="mt-10 flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => window.print()}
                  data-testid="bio-download"
                  className="btn-outline w-full justify-center"
                >
                  Download Profile <Download size={14} strokeWidth={1.5} />
                </button>
                <Link to="/leadership/contact" data-testid="bio-contact" className="btn-ink w-full justify-center">
                  Contact the Office
                </Link>
              </div>
            </div>
          </aside>

          {/* Body */}
          <div className="col-span-12 md:col-span-9">
            <Reveal>
              <img
                src={executive.headshot}
                alt={executive.headshotAlt}
                width="1200"
                height="1500"
                loading="lazy"
                className="w-full max-w-md h-[560px] object-cover duotone-portrait float-none md:float-right md:ml-8 md:mb-6"
              />
            </Reveal>

            <article className="prose-editorial">
              <Reveal as="section" id="overview">
                <p>{executive.bio[0]}</p>
              </Reveal>

              <Reveal as="section" id="career">
                <h2>Career Summary</h2>
                <p>{executive.bio[1]}</p>
                <p>{executive.bio[2]}</p>

                <ul className="mt-8 border-t border-[#E2E8F0]" data-testid="career-timeline">
                  {executive.priorFirms.map((f, i) => (
                    <li
                      key={f.name}
                      className="border-b border-[#E2E8F0] py-5 flex flex-col md:flex-row md:items-baseline md:justify-between gap-2"
                      style={{ listStyle: "none", paddingLeft: 0, marginBottom: 0 }}
                    >
                      <div className="flex items-baseline gap-6">
                        <span className="section-num text-[#8E7331]">{String(i + 1).padStart(2, "0")}</span>
                        <span className="h-editorial text-xl text-[#0A1128]">{f.name}</span>
                      </div>
                      <div className="flex items-baseline gap-6 md:gap-10 text-sm font-sans text-[#475569]">
                        <span>{f.role}</span>
                        <span className="uppercase tracking-[0.14em] text-[#0A1128]">{f.period}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </Reveal>

              <Reveal as="section" id="philosophy">
                <h2>Leadership Philosophy</h2>
                <p>{executive.bio[4]}</p>
                <p>{executive.bio[3]}</p>
              </Reveal>

              <Reveal as="section" id="expertise">
                <h2>Areas of Focus</h2>
                <p>Advisory disciplines applied across founder-led, family-office, and institutional contexts:</p>
                <ul>
                  {executive.expertise.map((e) => (<li key={e}>{e}</li>))}
                </ul>
                <div className="mt-6 no-print">
                  <Link to="/leadership/expertise" data-testid="bio-expertise-link" className="link-underline text-sm uppercase tracking-[0.16em] font-sans">
                    Explore Focus <ArrowUpRight size={14} className="inline" strokeWidth={1.5} />
                  </Link>
                </div>
              </Reveal>

              <Reveal as="section" id="beyond">
                <h2>Beyond the Executive Suite</h2>
                <p>{executive.bio[5]}</p>
              </Reveal>

              <Reveal as="section" id="today">
                <h2>Today</h2>
                <p>{executive.bio[6]}</p>
              </Reveal>
            </article>
          </div>
        </div>
      </section>
    </>
  );
}
